import React, { useMemo, useState } from "react";
import { AlertTriangle, Download, FileSearch, KeyRound, Loader2, Play, ShieldCheck } from "lucide-react";
import "./CognismScraper.css";

const DEFAULT_INPUT = "Nike\nAdidas\nPuma";

const parseCompanies = (value) =>
  value
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);

const csvEscape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const buildCsv = (rows) => {
  const headers = ["company", "status", "previewContacts", "message"];
  return [
    headers.join(","),
    ...rows.map((row) =>
      [
        csvEscape(row.company),
        csvEscape(row.status),
        csvEscape(row.previewContacts),
        csvEscape(row.message),
      ].join(",")
    ),
  ].join("\n");
};

const downloadCsv = (rows) => {
  const blob = new Blob([buildCsv(rows)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `cognism-preview-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export default function CognismScraper() {
  const [companiesInput, setCompaniesInput] = useState(DEFAULT_INPUT);
  const [companyLimit, setCompanyLimit] = useState(3);
  const [contactLimit, setContactLimit] = useState(10);
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");

  const companies = useMemo(() => parseCompanies(companiesInput), [companiesInput]);
  const selectedCompanies = useMemo(
    () => companies.slice(0, Math.max(1, Number(companyLimit) || 1)),
    [companies, companyLimit]
  );
  const canRun = selectedCompanies.length > 0 && !isRunning;

  const runPreview = async () => {
    if (!canRun) return;

    setIsRunning(true);
    setError("");
    setResults([]);

    try {
      const response = await fetch("/api/cognism/preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companies: selectedCompanies,
          previewLimit: Math.max(1, Math.min(25, Number(contactLimit) || 10)),
        }),
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload.error || "Preview request failed.");
      }

      setResults(payload.results || []);
    } catch (err) {
      setError(err.message || "Preview request failed.");
    } finally {
      setIsRunning(false);
    }
  };

  const totals = results.reduce(
    (acc, row) => {
      acc.companies += 1;
      acc.contacts += Number(row.previewContacts || 0);
      if (row.status === "ok") acc.ok += 1;
      return acc;
    },
    { companies: 0, contacts: 0, ok: 0 }
  );

  return (
    <main className="cognism-page">
      <section className="cognism-shell" aria-labelledby="cognism-title">
        <div className="cognism-header">
          <div>
            <p className="cognism-kicker">Snows ProAm internal tool</p>
            <h1 id="cognism-title">Cognism preview scraper</h1>
            <p className="cognism-lede">
              Run a limited company preview without redeeming contacts, revealing direct contact details, or storing an API key in the browser.
            </p>
          </div>

          <div className="cognism-trust-panel" aria-label="Preview safety rules">
            <div>
              <ShieldCheck aria-hidden="true" />
              <span>Preview only</span>
            </div>
            <div>
              <KeyRound aria-hidden="true" />
              <span>Server-side key</span>
            </div>
            <div>
              <FileSearch aria-hidden="true" />
              <span>No redeem calls</span>
            </div>
          </div>
        </div>

        <div className="cognism-layout">
          <form
            className="cognism-panel"
            onSubmit={(event) => {
              event.preventDefault();
              runPreview();
            }}
          >
            <div className="cognism-panel-heading">
              <h2>Preview setup</h2>
              <p>Paste company names or domains, one per line. The first test is capped by default.</p>
            </div>

            <label className="cognism-field">
              <span>Companies</span>
              <textarea
                value={companiesInput}
                onChange={(event) => setCompaniesInput(event.target.value)}
                rows={8}
                spellCheck="false"
                placeholder="Nike&#10;Adidas&#10;Puma"
                aria-describedby="company-help"
              />
            </label>
            <p id="company-help" className="cognism-help">
              {companies.length} parsed. This run will test {selectedCompanies.length} compan{selectedCompanies.length === 1 ? "y" : "ies"}.
            </p>

            <div className="cognism-controls">
              <label className="cognism-field">
                <span>Company test limit</span>
                <input
                  type="number"
                  min="1"
                  max="25"
                  value={companyLimit}
                  onChange={(event) => setCompanyLimit(event.target.value)}
                />
              </label>
              <label className="cognism-field">
                <span>Preview contacts per company</span>
                <input
                  type="number"
                  min="1"
                  max="25"
                  value={contactLimit}
                  onChange={(event) => setContactLimit(event.target.value)}
                />
              </label>
            </div>

            <div className="cognism-warning" role="note">
              <AlertTriangle aria-hidden="true" />
              <p>
                Add the key as <code>COGNISM_API_KEY</code> on the server. Do not paste it into this page or commit it to the repo.
              </p>
            </div>

            {error ? (
              <div className="cognism-error" role="alert">
                {error}
              </div>
            ) : null}

            <button type="submit" className="cognism-primary" disabled={!canRun}>
              {isRunning ? <Loader2 className="cognism-spin" aria-hidden="true" /> : <Play aria-hidden="true" />}
              <span>{isRunning ? "Running preview" : "Run preview test"}</span>
            </button>
          </form>

          <section className="cognism-panel" aria-labelledby="preview-results">
            <div className="cognism-panel-heading">
              <h2 id="preview-results">Results</h2>
              <p>Counts only. Emails and phone numbers are intentionally masked server-side.</p>
            </div>

            <div className="cognism-stats" aria-live="polite">
              <div>
                <span>{totals.companies}</span>
                <p>Companies checked</p>
              </div>
              <div>
                <span>{totals.contacts}</span>
                <p>Preview contacts</p>
              </div>
              <div>
                <span>{totals.ok}</span>
                <p>Successful previews</p>
              </div>
            </div>

            <div className="cognism-results-list">
              {isRunning ? (
                <div className="cognism-empty" aria-live="polite">
                  Checking the preview endpoint with the current limits...
                </div>
              ) : results.length ? (
                results.map((row) => (
                  <article className="cognism-result" key={row.company}>
                    <div>
                      <h3>{row.company}</h3>
                      <p>{row.message || "Preview complete."}</p>
                    </div>
                    <strong>{row.previewContacts || 0}</strong>
                  </article>
                ))
              ) : (
                <div className="cognism-empty">
                  Run a preview to see company-by-company counts here.
                </div>
              )}
            </div>

            <button
              type="button"
              className="cognism-secondary"
              disabled={!results.length}
              onClick={() => downloadCsv(results)}
            >
              <Download aria-hidden="true" />
              <span>Download CSV</span>
            </button>
          </section>
        </div>
      </section>
    </main>
  );
}
